import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, CheckCircle2, ImageOff, X, ZoomIn, ChevronLeft, ChevronRight, Download, FileText } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { Card, Spinner } from "@/components/ui";
import {
  expiryBand, bandStyle, fmtDate, isPdfPath,
  CATEGORY_DISPLAY, CATEGORY_ORDER,
  buildLicenceFilename,
} from "./licenceHelpers";
import { downloadFromStorage } from "@/lib/storageDownload";

// =============================================================================
// LicenceInspectorScreen — /licences/inspector
// Read-only, print-friendly, big-type view of all active licences. Designed
// to be shown to an HSE inspector on a phone at site gate.
//   - No sidebar navigation clutter
//   - Large text, high contrast
//   - Photos fetched via 1-hour signed URLs
//   - Colour-banded per James's spec (yellow ≤60d, red overdue)
// =============================================================================

interface LicenceRow {
  licence_id:      string;
  licence_number:  string | null;
  issue_date:      string | null;
  expiry_date:     string | null;
  state_issued:    string | null;
  verified_at:     string | null;
  front_photo_url: string | null;
  back_photo_url:  string | null;
  licence_type: {
    display_name:  string;
    category:      string;
    issuing_body:  string | null;
  } | null;
}

interface ZoomState {
  frontUrl?:    string;
  backUrl?:     string;
  label:        string;
  side:         "front" | "back";
  // T-13 — for the Download button: enough info to build a sensible filename.
  frontPath?:   string;
  backPath?:    string;
  expiryDate?:  string | null;
}

export default function LicenceInspectorScreen() {
  const user = useRequireAuth();
  const [signedFronts, setSignedFronts] = useState<Record<string, string>>({});
  const [signedBacks,  setSignedBacks]  = useState<Record<string, string>>({});
  const [zoom, setZoom] = useState<ZoomState | null>(null);

  // Lightbox keyboard nav: Esc to dismiss, ← → to flip front/back if both exist.
  useEffect(() => {
    if (!zoom) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setZoom(null);
      } else if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        // Flip side only if the other side has a URL.
        setZoom(prev => {
          if (!prev) return prev;
          if (prev.side === "front" && prev.backUrl)  return { ...prev, side: "back" };
          if (prev.side === "back"  && prev.frontUrl) return { ...prev, side: "front" };
          return prev;
        });
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [zoom]);

  const { data: licences, isLoading } = useQuery({
    queryKey: ["my_licences_inspector", user?.employee_id],
    enabled: !!user?.employee_id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("employee_licences")
        .select(`
          licence_id, licence_number, issue_date, expiry_date, state_issued,
          verified_at, front_photo_url, back_photo_url,
          licence_type:licence_types (display_name, category, issuing_body)
        `)
        .eq("employee_id", user!.employee_id!)
        .eq("is_active", true)
        .order("expiry_date", { ascending: true, nullsFirst: false });
      if (error) throw error;
      return (data ?? []) as unknown as LicenceRow[];
    },
  });

  // Batch-fetch signed URLs for both front AND back photos on mount.
  // 1-hour TTL; stays valid for a whole site visit. Back photos surface on
  // the row's secondary tile and inside the lightbox arrow nav.
  useEffect(() => {
    if (!licences) return;
    const frontPaths = licences.map(l => l.front_photo_url).filter((p): p is string => !!p);
    const backPaths  = licences.map(l => l.back_photo_url).filter((p): p is string => !!p);
    const allPaths   = [...frontPaths, ...backPaths];
    if (allPaths.length === 0) return;

    const frontSet = new Set(frontPaths);
    const backSet  = new Set(backPaths);

    let cancelled = false;
    (async () => {
      const { data } = await supabase.storage
        .from("licence-documents")
        .createSignedUrls(allPaths, 3600);
      if (cancelled || !data) return;
      const fMap: Record<string, string> = {};
      const bMap: Record<string, string> = {};
      for (const item of data) {
        if (!item.path || !item.signedUrl) continue;
        if (frontSet.has(item.path)) fMap[item.path] = item.signedUrl;
        if (backSet.has(item.path))  bMap[item.path] = item.signedUrl;
      }
      setSignedFronts(fMap);
      setSignedBacks(bMap);
    })();

    return () => { cancelled = true; };
  }, [licences]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink">
        <Spinner size={24} />
      </div>
    );
  }

  const list = licences ?? [];

  // Group by category, in order
  const byCategory = new Map<string, LicenceRow[]>();
  for (const l of list) {
    const cat = l.licence_type?.category ?? "other";
    if (!byCategory.has(cat)) byCategory.set(cat, []);
    byCategory.get(cat)!.push(l);
  }

  return (
    <div className="min-h-screen bg-ink text-text">
      <div className="max-w-3xl mx-auto px-4 py-4">
        <Link
          to="/licences"
          className="inline-flex items-center gap-1 text-sm text-text-muted hover:text-text"
        >
          <ArrowLeft size={14} /> Back
        </Link>
      </div>

      <header className="max-w-3xl mx-auto px-4 pb-4 border-b border-line">
        <h1 className="text-3xl font-bold tracking-tight">Inspector view</h1>
        <p className="text-text-muted mt-1">
          {user?.first_name ? `${user.first_name} ${user.last_name ?? ""}`.trim() : "Current user"}
          {" · "}
          {list.length} licence{list.length === 1 ? "" : "s"} on record
        </p>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {list.length === 0 ? (
          <Card>
            <div className="p-8 text-center text-text-muted">
              No licences on record.
            </div>
          </Card>
        ) : (
          CATEGORY_ORDER
            .filter(cat => byCategory.has(cat))
            .map(cat => (
              <section key={cat}>
                <h2 className="text-[11px] font-semibold text-text-muted uppercase tracking-wider2 mb-3">
                  {CATEGORY_DISPLAY[cat] ?? cat}
                </h2>
                <div className="space-y-3">
                  {byCategory.get(cat)!.map(l => {
                    const frontUrl = l.front_photo_url ? signedFronts[l.front_photo_url] : undefined;
                    const backUrl  = l.back_photo_url  ? signedBacks[l.back_photo_url]   : undefined;
                    const label    = l.licence_type?.display_name ?? "Licence photo";
                    return (
                      <InspectorRow
                        key={l.licence_id}
                        licence={l}
                        frontUrl={frontUrl}
                        backUrl={backUrl}
                        onZoom={(side) => setZoom({
                          frontUrl, backUrl, label, side,
                          frontPath:  l.front_photo_url,
                          backPath:   l.back_photo_url,
                          expiryDate: l.expiry_date,
                        })}
                      />
                    );
                  })}
                </div>
              </section>
            ))
        )}
      </main>

      <footer className="max-w-3xl mx-auto px-4 py-6 text-center text-[11px] text-text-muted">
        Generated {new Date().toLocaleString("en-AU", {
          day: "2-digit", month: "short", year: "numeric",
          hour: "2-digit", minute: "2-digit",
        })}
        {" · "}Coretel Group
      </footer>

      {/* Lightbox — tap image to enlarge, tap anywhere or Esc to dismiss.
          When both front + back exist, arrow buttons + ← → keys flip sides. */}
      {zoom && (() => {
        const currentUrl = zoom.side === "front" ? zoom.frontUrl : zoom.backUrl;
        const hasOther   = zoom.side === "front" ? !!zoom.backUrl : !!zoom.frontUrl;
        const flip = () => setZoom(z => z ? ({ ...z, side: z.side === "front" ? "back" : "front" }) : z);
        return (
          <div
            className="fixed inset-0 bg-black/90 z-50 flex flex-col"
            onClick={() => setZoom(null)}
            role="dialog"
            aria-label={zoom.label}
          >
            <div className="flex items-center justify-between p-4 text-white gap-3">
              <div className="text-sm font-medium truncate flex-1 min-w-0">
                {zoom.label}
                <span className="ml-2 text-white/60 font-normal">
                  · {zoom.side === "front" ? "Front" : "Back"}
                  {hasOther && <> of 2</>}
                </span>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    const path = zoom.side === "front" ? zoom.frontPath : zoom.backPath;
                    if (!path) return;
                    try {
                      const filename = buildLicenceFilename({
                        licence_type_display: zoom.label,
                        holder_first_name:    user?.first_name ?? null,
                        holder_last_name:     user?.last_name ?? null,
                        expiry_date:          zoom.expiryDate ?? null,
                        side:                 zoom.side,
                        storage_path:         path,
                      });
                      await downloadFromStorage("licence-documents", path, filename);
                    } catch (err: any) {
                      alert(`Download failed: ${err?.message ?? err}`);
                    }
                  }}
                  className="p-2 rounded hover:bg-white/10 inline-flex items-center gap-1.5 text-sm"
                  aria-label="Download"
                  title="Download with proper filename"
                >
                  <Download size={18} />
                  <span className="hidden sm:inline">Download</span>
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setZoom(null); }}
                  className="p-2 rounded hover:bg-white/10"
                  aria-label="Close"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="flex-1 flex items-center justify-center p-4 min-h-0 relative">
              {/* Left arrow — only when there's another side and we're not on front */}
              {hasOther && zoom.side === "back" && (
                <button
                  onClick={(e) => { e.stopPropagation(); flip(); }}
                  className="absolute left-2 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white"
                  aria-label="Show front"
                >
                  <ChevronLeft size={28} />
                </button>
              )}
              {currentUrl ? (
                isPdfPath(currentUrl) ? (
                  /* T-391 — PDF certs render in an iframe, not <img> */
                  <iframe
                    src={currentUrl}
                    title={`${zoom.label} ${zoom.side}`}
                    className="w-full h-full rounded bg-white"
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <img
                    src={currentUrl}
                    alt={`${zoom.label} ${zoom.side}`}
                    className="max-w-full max-h-full object-contain select-none"
                    onClick={(e) => e.stopPropagation()}
                  />
                )
              ) : (
                <div className="text-white/70 text-sm">No {zoom.side} photo on file.</div>
              )}
              {/* Right arrow */}
              {hasOther && zoom.side === "front" && (
                <button
                  onClick={(e) => { e.stopPropagation(); flip(); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white"
                  aria-label="Show back"
                >
                  <ChevronRight size={28} />
                </button>
              )}
            </div>
            {/* Front/Back toggle pills + close hint */}
            <div className="text-center pb-4">
              {hasOther && (
                <div
                  className="inline-flex items-center gap-1 bg-white/10 rounded-full p-1 mb-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() => setZoom(z => z ? ({ ...z, side: "front" }) : z)}
                    disabled={!zoom.frontUrl}
                    className={`px-3 py-1 text-xs rounded-full font-medium ${
                      zoom.side === "front" ? "bg-white text-black" : "text-white/80 hover:text-white"
                    } ${!zoom.frontUrl ? "opacity-40 cursor-not-allowed" : ""}`}
                  >
                    Front
                  </button>
                  <button
                    onClick={() => setZoom(z => z ? ({ ...z, side: "back" }) : z)}
                    disabled={!zoom.backUrl}
                    className={`px-3 py-1 text-xs rounded-full font-medium ${
                      zoom.side === "back" ? "bg-white text-black" : "text-white/80 hover:text-white"
                    } ${!zoom.backUrl ? "opacity-40 cursor-not-allowed" : ""}`}
                  >
                    Back
                  </button>
                </div>
              )}
              <div className="text-white/60 text-xs">
                {hasOther ? "Tap outside or Esc to close · ← → to flip" : "Tap outside or Esc to close"}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ---------------------------------------------------------------------------

interface InspectorRowProps {
  licence:  LicenceRow;
  frontUrl: string | undefined;
  backUrl:  string | undefined;
  onZoom:   (side: "front" | "back") => void;
}

function InspectorRow({ licence, frontUrl, backUrl, onZoom }: InspectorRowProps) {
  const band  = expiryBand(licence.expiry_date);
  const style = bandStyle(band);
  const isVerified = !!licence.verified_at;
  const label = licence.licence_type?.display_name ?? "Licence photo";

  return (
    <Card className={style.cardBg}>
      <div className="p-4 flex gap-4">
        {/* Photo column — front primary, back stacked beneath if it exists.
            Tapping either opens the lightbox with arrow nav. */}
        <div className="flex flex-col gap-1.5 flex-shrink-0">
          {frontUrl ? (
            <button
              type="button"
              onClick={() => onZoom("front")}
              className="w-24 h-24 rounded border border-line bg-ink-raised overflow-hidden relative group cursor-zoom-in"
              aria-label={`Enlarge ${label} front`}
            >
              {isPdfPath(frontUrl) ? (
                <div className="w-full h-full flex flex-col items-center justify-center gap-1">
                  <FileText size={24} className="text-accent"/>
                  <span className="text-[9px] font-mono uppercase text-text-muted">PDF cert</span>
                </div>
              ) : (
                <img src={frontUrl} alt={`${label} front`} className="w-full h-full object-cover"/>
              )}
              <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors">
                <ZoomIn size={22} className="text-white opacity-0 group-hover:opacity-100 transition-opacity"/>
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-black/55 text-white text-[9px] uppercase tracking-wider2 text-center py-0.5">
                Front
              </div>
            </button>
          ) : (
            <div className="w-24 h-24 rounded border border-line bg-ink-raised overflow-hidden flex items-center justify-center">
              <ImageOff size={20} className="text-text-muted" />
            </div>
          )}
          {backUrl && (
            <button
              type="button"
              onClick={() => onZoom("back")}
              className="w-24 h-16 rounded border border-line bg-ink-raised overflow-hidden relative group cursor-zoom-in"
              aria-label={`Enlarge ${label} back`}
            >
              {isPdfPath(backUrl) ? (
                <div className="w-full h-full flex flex-col items-center justify-center gap-1">
                  <FileText size={18} className="text-accent"/>
                  <span className="text-[9px] font-mono uppercase text-text-muted">PDF</span>
                </div>
              ) : (
                <img src={backUrl} alt={`${label} back`} className="w-full h-full object-cover"/>
              )}
              <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors">
                <ZoomIn size={16} className="text-white opacity-0 group-hover:opacity-100 transition-opacity"/>
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-black/55 text-white text-[9px] uppercase tracking-wider2 text-center py-0.5">
                Back
              </div>
            </button>
          )}
        </div>
        <div className="min-w-0 flex-1 flex flex-col justify-between">
          <div>
            <div className="font-semibold text-base leading-tight">
              {licence.licence_type?.display_name ?? "Unknown type"}
            </div>
            {licence.licence_number && (
              <div className="text-sm text-text-muted font-mono mt-0.5">
                {licence.licence_number}
                {licence.state_issued && <> · {licence.state_issued}</>}
              </div>
            )}
            {licence.licence_type?.issuing_body && (
              <div className="text-[11px] text-text-muted mt-0.5">
                Issued by {licence.licence_type.issuing_body}
              </div>
            )}
          </div>
          <div className="flex items-center justify-between mt-2">
            <div className={`text-sm font-semibold ${style.text}`}>
              {licence.expiry_date
                ? <>Expires {fmtDate(licence.expiry_date)} · {style.label}</>
                : <>{style.label}</>
              }
            </div>
            {isVerified && (
              <div className="flex items-center gap-1 text-[11px] text-ok font-mono uppercase tracking-wider2">
                <CheckCircle2 size={12} /> Verified
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
